import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface MarkdownTextProps {
  content: string;
  className?: string;
}

/** Joins a line that is only `1.` / `2.` with the following non-empty line so lists render as one block. */
function mergeOrphanNumberedLinesInContent(content: string): string {
  const lines = content.split('\n');
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const trimmed = (lines[i] ?? '').trim();
    if (/^\d{1,2}\.\s*$/.test(trimmed)) {
      let j = i + 1;
      while (j < lines.length && !(lines[j] ?? '').trim()) j += 1;
      if (j < lines.length) {
        const next = (lines[j] ?? '').trim();
        if (next && !/^\d{1,2}\.\s/.test(next)) {
          out.push(`${trimmed} ${next}`);
          i = j + 1;
          continue;
        }
      }
    }
    out.push(lines[i] ?? '');
    i += 1;
  }
  return out.join('\n');
}

function sanitizeHref(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) return trimmed;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:' || parsed.protocol === 'mailto:' || parsed.protocol === 'tel:') {
      return parsed.toString();
    }
  } catch {
    return null;
  }
  return null;
}

function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(<span key={`t-${key++}`}>{text.slice(lastIndex, match.index)}</span>);
    }
    const token = match[0];
    if (token.startsWith('**') && token.endsWith('**')) {
      nodes.push(<strong key={`b-${key++}`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`') && token.endsWith('`')) {
      nodes.push(
        <code key={`c-${key++}`} className="rounded bg-card-hover/60 px-1 py-0.5 text-[0.9em] dark:bg-card-hover/70">
          {token.slice(1, -1)}
        </code>
      );
    } else if (token.startsWith('[') && token.includes('](') && token.endsWith(')')) {
      const splitIdx = token.indexOf('](');
      const label = token.slice(1, splitIdx);
      const href = token.slice(splitIdx + 2, -1);
      const safeHref = sanitizeHref(href);
      if (!safeHref) {
        nodes.push(<span key={`u-${key++}`}>{label}</span>);
        lastIndex = regex.lastIndex;
        continue;
      }
      nodes.push(
        <a
          key={`a-${key++}`}
          href={safeHref}
          target="_blank"
          rel="noreferrer noopener nofollow"
          className="underline underline-offset-2 hover:opacity-80"
        >
          {label}
        </a>
      );
    } else {
      nodes.push(<span key={`u-${key++}`}>{token}</span>);
    }
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(<span key={`t-${key++}`}>{text.slice(lastIndex)}</span>);
  }
  return nodes;
}

export function MarkdownText({ content, className }: MarkdownTextProps) {
  const lines = mergeOrphanNumberedLinesInContent(content).split('\n');
  const elements: ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = (lines[i] ?? '').trimEnd();
    if (!line.trim()) {
      i += 1;
      continue;
    }

    if (line.startsWith('### ')) {
      elements.push(
        <h3 key={`h3-${i}`} className="mb-1 mt-2 text-base font-semibold">
          {renderInline(line.slice(4))}
        </h3>
      );
      i += 1;
      continue;
    }
    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={`h2-${i}`} className="mb-1 mt-2 text-lg font-semibold">
          {renderInline(line.slice(3))}
        </h2>
      );
      i += 1;
      continue;
    }
    if (line.startsWith('# ')) {
      elements.push(
        <h1 key={`h1-${i}`} className="mb-1 mt-2 text-xl font-bold">
          {renderInline(line.slice(2))}
        </h1>
      );
      i += 1;
      continue;
    }

    if (line.startsWith('- ') || line.startsWith('* ')) {
      const listItems: ReactNode[] = [];
      while (i < lines.length && (((lines[i] ?? '').trimStart().startsWith('- ')) || ((lines[i] ?? '').trimStart().startsWith('* ')))) {
        const current = (lines[i] ?? '').trimStart();
        const item = current.slice(2);
        listItems.push(<li key={`li-${i}`}>{renderInline(item)}</li>);
        i += 1;
      }
      elements.push(
        <ul key={`ul-${i}`} className="list-disc space-y-1.5 pl-5 leading-relaxed text-[0.9375rem]">
          {listItems}
        </ul>
      );
      continue;
    }

    const trimmedStart = line.trimStart();
    if (/^\d{1,2}\.\s+/.test(trimmedStart)) {
      const listStart = i;
      const listItems: ReactNode[] = [];
      while (i < lines.length) {
        const cur = (lines[i] ?? '').trimStart();
        if (!cur.trim()) {
          i += 1;
          continue;
        }
        const numMatch = cur.match(/^(\d{1,2})\.\s+(.*)$/);
        if (!numMatch) break;
        listItems.push(<li key={`oli-${i}`}>{renderInline(numMatch[2] ?? '')}</li>);
        i += 1;
      }
      elements.push(
        <ol
          key={`ol-${listStart}`}
          className="list-decimal space-y-1.5 pl-5 leading-relaxed text-[0.9375rem] [list-style-position:outside] [&_li]:break-inside-avoid"
        >
          {listItems}
        </ol>
      );
      continue;
    }

    elements.push(
      <p key={`p-${i}`} className="whitespace-pre-wrap leading-relaxed text-[0.9375rem]">
        {renderInline(line)}
      </p>
    );
    i += 1;
  }

  return <div className={cn('max-w-none [&_h1]:mb-2 [&_h2]:mb-2 [&_h2]:mt-1 [&_h3]:mb-1 [&_h3]:mt-1', className)}>{elements}</div>;
}
